/**
 * @file Data quality (`#/data-quality`) — admin only.
 *
 * Three rollups stacked vertically: Unassigned, Orphan, In storage. Each
 * one a live Firestore query against /devices with a small table. Per UI
 * spec — its own tab (not inside Settings) so IT actually checks it.
 *
 * The "fix" path for everything is in Flexi, not here. Each row has an
 * "Open in Flexi ↗" deep link and a "Mark retired" local action.
 */

import { el, clear, formatDate, toast } from '../utils.js';
import { categoryIcon }          from '../icons.js';
import { CATEGORIES, flexiWebUrl } from '../config.js';
import { navigate }              from '../router.js';
import {
  watchDevices, updateDevice,
  watchFormSubmissions, linkFormSubmission, ignoreFormSubmission,
} from '../db.js';
import { openEmployeePicker }    from '../employee-picker.js';
import { getCurrentUser }        from '../auth.js';
import { renderPageHead }        from '../page-header.js';
import { logAuditEvent }         from '../audit-log.js';
import { AUDIT_ACTION }          from '../lib/audit-actions.js';
import { sortableTable }         from '../lib/sortable-table.js';
import { copyable } from '../lib/copyable.js';
import { confirmDialog } from '../standard/js/dialog.js';

export function renderDataQuality(host) {
  clear(host);

  host.appendChild(renderPageHead(
    'Data quality',
    "Devices Gear found odd. Fix the source in Abra (the next sync brings it in), or mark retired here if the device really is gone.",
  ));

  const sections = el('div', { class: 'stack' });
  host.appendChild(sections);

  // ── Pending joining-form submissions ─────────────────────────────────
  const formsCard = makeRollupCard('Pending joining-form submissions',
    'Rows from the Google Form that the sync couldn\'t auto-match to a BambooHR employee. Pick the right person manually, or dismiss spam/duplicates.');
  sections.appendChild(formsCard.card);
  const unsubForms = watchFormSubmissions({ matchStatus: 'unmatched' }, (subs) => {
    formsCard.renderForms(subs);
  });

  // ── Unassigned: in_use but no holder, no orphan tag ──────────────────
  const unassignedCard = makeRollupCard('Unassigned', 'Devices Flexi knows about but with no holder tag — assign or retire.');
  sections.appendChild(unassignedCard.card);
  const unsubUnassigned = watchDevices(
    { status: 'in_use' },
    (devices) => {
      const filtered = devices.filter((d) => !d.currentHolder && !d.holderUnresolved);
      unassignedCard.render(filtered, { kind: 'unassigned' });
    },
  );

  // ── Orphan: parsed an employee number that doesn't match Bamboo ──────
  const orphanCard = makeRollupCard('Orphan', "Devices tagged to an employee number that doesn't match anyone in BambooHR. The tag in Abra is stale.");
  sections.appendChild(orphanCard.card);
  const unsubOrphan = watchDevices(
    { holderUnresolved: true },
    (devices) => orphanCard.render(devices, { kind: 'orphan' }),
  );

  // ── In storage (informational) ───────────────────────────────────────
  const storageCard = makeRollupCard('In storage', 'Devices marked as sitting in storage (per the Flexi tag). Not a problem — just visibility.');
  sections.appendChild(storageCard.card);
  const unsubStorage = watchDevices(
    { contextTag: 'Storage' },
    (devices) => storageCard.render(devices, { kind: 'storage' }),
  );

  host.addEventListener('cleanup', () => {
    unsubForms();
    unsubUnassigned();
    unsubOrphan();
    unsubStorage();
  }, { once: true });
}

function makeRollupCard(title, subtitle) {
  const headEl   = el('div', { class: 'section-head' }, title, el('span', { class: 'count' }, '(–)'));
  const subEl    = el('p', { class: 'muted', style: 'margin-top:0' }, subtitle);
  const tableHost = el('div');
  const card = el('section', { class: 'card' }, headEl, subEl, tableHost);

  function render(devices, opts) {
    headEl.querySelector('.count').textContent = `(${devices.length})`;
    clear(tableHost);
    if (!devices.length) {
      tableHost.appendChild(el('p', { class: 'muted', style: 'margin-top:12px' }, 'All clear.'));
      return;
    }
    tableHost.appendChild(sortableTable({
      columns: [
        { label: 'Category', sortValue: (d) => (CATEGORIES[d.category]?.label || d.category) },
        { label: 'Name',     sortValue: (d) => d.name },
        { label: 'SN',       sortValue: (d) => d.sn },
        ...(opts.kind === 'orphan'
          ? [{ label: 'Tag', sortValue: (d) => d.holderEmployeeNumberRaw }]
          : []),
        { label: 'Updated',  sortValue: (d) => d.updatedAt },
        { label: '', sortable: false, thStyle: 'width:200px;text-align:right' },
      ],
      rows: devices,
      renderRow: (d) => renderRow(d, opts),
      initialSort: { index: 0, dir: 'asc' }, // grouped by category
    }));
  }

  function renderRow(d, opts) {
    const cat = CATEGORIES[d.category] || { label: d.category };
    return el('tr', null,
      el('td', null, el('span', { style: 'display:inline-flex;align-items:center;gap:6px' }, categoryIcon(d.category), cat.label)),
      el('td', { onClick: () => navigate(`/devices/${d.id}`), style: 'cursor:pointer' }, d.name ? copyable(d.name, { label: 'name' }) : '—'),
      el('td', null, d.sn ? copyable(d.sn, { label: 'SN', mono: true, style: 'color:var(--color-text-secondary)' }) : '—'),
      opts.kind === 'orphan'
        ? el('td', null, el('span', { class: 'chip chip-error' }, d.holderEmployeeNumberRaw || '?'))
        : null,
      el('td', { class: 'muted' }, formatDate(d.updatedAt)),
      el('td', { style: 'text-align:right' },
        d.flexiKod
          ? el('a', {
              href: flexiWebUrl(d.flexiKod),
              target: '_blank', rel: 'noopener noreferrer',
              class: 'btn btn-sm', style: 'margin-right:6px',
              onClick: (e) => e.stopPropagation(),
            }, 'Open in Flexi ↗')
          : null,
        el('button', {
          class: 'btn btn-ghost btn-sm',
          onClick: async (e) => {
            e.stopPropagation();
            if (!await confirmDialog({ title: `Mark "${d.name || d.flexiKod || d.id}" retired?`, body: 'Gear only — Abra is not changed.', confirm: 'Mark retired', danger: true })) return;
            try {
              // gearRetired is an ADMIN-OWNED field materialise carries
              // forward; writing `status` directly was silently reverted by
              // the next sync (which rebuilds status from Abra).
              await updateDevice(d.id, { gearRetired: true, status: 'retired' });
              toast('Device retired in Gear.', 'success');
            } catch (err) {
              toast(err.message || 'Could not retire.', 'error');
            }
          },
        }, 'Retire'),
      ),
    );
  }

  function renderForms(submissions) {
    headEl.querySelector('.count').textContent = `(${submissions.length})`;
    clear(tableHost);
    if (!submissions.length) {
      tableHost.appendChild(el('p', { class: 'muted', style: 'margin-top:12px' }, 'All clear.'));
      return;
    }
    tableHost.appendChild(sortableTable({
      columns: [
        { label: 'Submitted',     sortValue: (s) => s.timestamp },
        { label: 'Name on form',  sortValue: (s) => s.fullName },
        { label: 'Private email', sortValue: (s) => s.privateEmail },
        { label: 'Items',         sortValue: (s) => (s.items || []).length },
        { label: '', sortable: false, thStyle: 'width:240px;text-align:right' },
      ],
      rows: submissions,
      renderRow: renderFormRow,
      initialSort: { index: 0, dir: 'desc' }, // newest submission first
    }));
  }

  function renderFormRow(s) {
    return el('tr', null,
      el('td', { class: 'muted' }, formatDate(s.timestamp)),
      el('td', null, s.fullName || '-'),
      el('td', { class: 'muted' }, s.privateEmail || '-'),
      el('td', { class: 'muted' }, `${(s.items || []).length} item${(s.items || []).length === 1 ? '' : 's'}`),
      el('td', { style: 'text-align:right' },
        el('button', {
          class: 'btn btn-sm',
          style: 'margin-right:6px',
          onClick: () => openLinkPicker(s),
        }, 'Link to...'),
        el('button', {
          class: 'btn btn-ghost btn-sm',
          onClick: async () => {
            if (!await confirmDialog({ title: `Dismiss "${s.fullName || '(no name)'}"?`, body: 'Stays in the audit log.', confirm: 'Dismiss', danger: true })) return;
            try {
              await ignoreFormSubmission(s.id, getCurrentUser()?.email);
              logAuditEvent({
                action: AUDIT_ACTION.ONBOARDING_FORM_IGNORE,
                subject: { type: 'form_submission', id: s.id, label: s.fullName || s.privateEmail || s.id },
                details: {
                  submissionId: s.id,
                  fullName:     s.fullName || null,
                  privateEmail: s.privateEmail || null,
                  itemCount:    (s.items || []).length,
                },
              });
              toast('Dismissed.', 'success');
            } catch (err) {
              toast(err.message || 'Could not dismiss.', 'error');
            }
          },
        }, 'Dismiss'),
      ),
    );
  }

  return { card, render, renderForms };
}

function openLinkPicker(submission) {
  openEmployeePicker({
    title:        'Link form submission',
    subtitle:     `"${submission.fullName || '(no name)'}" — ${submission.privateEmail || 'no email'} — ${(submission.items || []).length} items.`,
    confirmLabel: 'Link & import items',
    onPick: async (employee) => {
      try {
        await linkFormSubmission({
          submissionId: submission.id,
          employeeId:   employee.id,
          employeeName: employee.fullName || null,
          items:        submission.items || [],
          actorEmail:   getCurrentUser()?.email,
        });
        logAuditEvent({
          action: AUDIT_ACTION.ONBOARDING_FORM_MATCH,
          subject: { type: 'employee', id: employee.id, label: employee.fullName || employee.email || employee.id },
          people: [employee.email],
          details: {
            submissionId:  submission.id,
            formFullName:  submission.fullName || null,
            privateEmail:  submission.privateEmail || null,
            itemCount:     (submission.items || []).length,
            // Capture the categories handed to the onboarding doc so we
            // can see at a glance what was imported.
            categories:    (submission.items || []).map((i) => i.category).filter(Boolean),
          },
        });
        toast(`Linked to ${employee.fullName || employee.email}.`, 'success');
      } catch (err) {
        toast(err.message || 'Link failed.', 'error');
      }
    },
  });
}
